/**
 * Auth API — typed wrappers around `/api/auth/*`.
 *
 * Scope (per T3): HTTP-only. OAuth redirect mechanics, window.location
 * handling, persistent session bootstrap live in T7 (web app shell).
 *
 * Legacy replaced magic-link auth with Google OAuth + JWT. No magic-link
 * endpoints exist on the server.
 */

import type { HttpClient } from './client';
import {
  type AuthIdentitiesResponse,
  type AuthSuccessResponse,
  type EmailLoginRequest,
  type EmailLoginResponse,
  type GenericInboxResponse,
  type GoogleAuthRequest,
  type GoogleAuthOutcome,
  type LogoutResponse,
  type RefreshResponse,
  type RegisterRequest,
  type RegisterResponse,
  type RequestResetRequest,
  type ResendVerificationResponse,
  type ResetRequest,
  type ResetResponse,
  type SetPasswordRequest,
  type User,
  type VerifyRequest,
  type VerifyResponse,
  authIdentitiesResponseSchema,
  authSuccessResponseSchema,
  emailLoginResponseSchema,
  genericInboxResponseSchema,
  googleAuthOutcomeSchema,
  logoutResponseSchema,
  refreshResponseSchema,
  registerResponseSchema,
  resendVerificationResponseSchema,
  resetResponseSchema,
  userSchema,
  verifyResponseSchema,
} from '../types/user';

export class AuthApi {
  constructor(private readonly http: HttpClient) {}

  /**
   * Exchange a Google ID token for a Shopista session.
   *
   * - `credential`: the Google ID token the browser received from the
   *   Google Identity Services button.
   * - `inviteToken`: optional household invite token (accept invite during
   *   signup).
   *
   * On success the backend sets the refresh-token HttpOnly cookie and returns
   * the short-lived access token in the response body.
   *
   * T49.2: a NEW account requires explicit Terms + Privacy consent. When the
   * caller signs in a new user without `termsAccepted`, the backend returns
   * `{ consentRequired: true }` instead of a session — so the result is a UNION
   * the caller branches on (`'consentRequired' in result`). A returning user
   * always gets the session body.
   */
  async loginWithGoogle(input: GoogleAuthRequest): Promise<GoogleAuthOutcome> {
    return this.http.post('/api/auth/google', input, googleAuthOutcomeSchema);
  }

  /**
   * Sign in with email + password (T27.2.10.1). Issues the same session as the
   * Google flow (both run through the backend's shared `issueSession`). On a
   * wrong password / unknown email the backend returns a generic 401 surfaced
   * here as an `ApiError`.
   */
  async loginWithEmail(input: EmailLoginRequest): Promise<EmailLoginResponse> {
    return this.http.post('/api/auth/login', input, emailLoginResponseSchema);
  }

  /**
   * Refresh the access token using the refresh cookie.
   *
   * The cookie is sent automatically when the client was constructed with
   * `withCredentials: true` (the default). Normally the HTTP client calls
   * this on its own when it sees a 401 — direct callers are only for session
   * bootstrap / manual recovery flows.
   */
  async refresh(): Promise<RefreshResponse> {
    return this.http.post('/api/auth/refresh', {}, refreshResponseSchema);
  }

  /**
   * Revoke the current session and clear the refresh cookie on the server.
   */
  async logout(): Promise<LogoutResponse> {
    return this.http.post('/api/auth/logout', {}, logoutResponseSchema);
  }

  /**
   * Return the currently authenticated user (requires a valid access token).
   */
  async me(): Promise<User> {
    return this.http.get('/api/auth/me', userSchema);
  }

  /**
   * The sign-in methods linked to the authenticated account (T27.2.10.1) —
   * drives the Settings link/unlink controls.
   */
  async identities(): Promise<AuthIdentitiesResponse> {
    return this.http.get('/api/auth/identities', authIdentitiesResponseSchema);
  }

  /**
   * Set (or change) the password on the SIGNED-IN account (T27.2.10.1). The
   * active session proves email ownership — no email check needed.
   */
  async setPassword(input: SetPasswordRequest): Promise<AuthSuccessResponse> {
    return this.http.post('/api/auth/set-password', input, authSuccessResponseSchema);
  }

  /**
   * Unlink a sign-in method (`'password'` | `'google'`). The server blocks
   * removing the LAST identity (409) — the caller surfaces the reason.
   */
  async unlinkIdentity(provider: 'password' | 'google'): Promise<void> {
    await this.http.delete(`/api/auth/identities/${provider}`);
  }

  // -------------------------------------------------------------------------
  // T27.2.10.1 Phase B — public registration + verification + reset.
  // -------------------------------------------------------------------------

  /**
   * Public email + password sign-up (T48). The backend verifies the Turnstile
   * token, PROVISIONS the account (unverified) and issues a session — so this
   * returns the SAME session body as login and the caller enters the app
   * directly. An already-registered email returns a 409 (surfaced as an
   * `ApiError`), routing the user to sign in / reset. A verify email is sent in
   * the background; confirming it is an in-app nudge, not a wall.
   */
  async register(input: RegisterRequest): Promise<RegisterResponse> {
    return this.http.post('/api/auth/register', input, registerResponseSchema);
  }

  /**
   * Re-send the email-confirmation link for the SIGNED-IN account (T48). Drives
   * the in-app confirm-email nudge's "Resend". Requires a session; throttled
   * server-side. Returns a generic ack.
   */
  async resendVerification(): Promise<ResendVerificationResponse> {
    return this.http.post('/api/auth/resend-verification', {}, resendVerificationResponseSchema);
  }

  /**
   * Consume an emailed verification token. On success the account is created and
   * a session is issued (same shape as the Google/email login response).
   */
  async verify(input: VerifyRequest): Promise<VerifyResponse> {
    return this.http.post('/api/auth/verify', input, verifyResponseSchema);
  }

  /**
   * Request a password-reset email. Non-enumerating: the same generic response
   * whether or not the email has an account.
   */
  async requestReset(input: RequestResetRequest): Promise<GenericInboxResponse> {
    return this.http.post('/api/auth/request-reset', input, genericInboxResponseSchema);
  }

  /**
   * Complete a password reset with an emailed token + the new password. The
   * backend revokes the account's other sessions and issues a fresh one (same
   * shape as login).
   */
  async reset(input: ResetRequest): Promise<ResetResponse> {
    return this.http.post('/api/auth/reset', input, resetResponseSchema);
  }
}
